import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL, { getImageUrl } from '../config';
import './GuestDashboard.css';
import { Volume2, VolumeX, Music } from 'lucide-react';

interface Post {
    _id: string;
    caption: string;
    imageUrl?: string;
    adminId: any;
    weddingId: any;
    createdAt: string;
    platform: string;
    isBlessing?: boolean;
    guestName?: string;
}

const GuestDashboard: React.FC = () => {
    const { accessToken, user, setUser, loading: authLoading } = useAuth();
    const history = useHistory();
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [weddingData, setWeddingData] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    const [shagunModalOpen, setShagunModalOpen] = useState(false);
    const [shagunAmount, setShagunAmount] = useState('501');
    const [shagunNote, setShagunNote] = useState('');
    const [isMusicPlaying, setIsMusicPlaying] = useState(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);

    // Social Cheer System state
    const [reactionsByPost, setReactionsByPost] = useState<Record<string, any[]>>({});
    const [cheersByPost, setCheersByPost] = useState<Record<string, number>>({});
    const [cheerParticles, setCheerParticles] = useState<{ id: number; postId: string; emoji: string; x: number; delay: number }[]>([]);
    const cheerIdRef = useRef(0);

    const REACTION_TYPES = [
        { type: 'love', emoji: '❤️', label: 'Love' },
        { type: 'oye_hoye', emoji: '🔥', label: 'Oye Hoye!' },
        { type: 'shabaash', emoji: '👏', label: 'Shabaash!' },
        { type: 'congrats', emoji: '🎉', label: 'Congrats!' },
        { type: 'fire', emoji: '💥', label: 'Lit!' },
    ];

    const fetchFeedData = useCallback(async (isInitial = false) => {
        try {
            if (isInitial) setLoading(true);

            // Fetch Feed and Wedding Details in parallel
            const feedPromise = fetch(`${API_BASE_URL}/api/posts/feed`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            }).then(res => res.json());

            const weddingPromise = fetch(`${API_BASE_URL}/api/wedding/current`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            }).then(res => res.json());

            const profilePromise = (isInitial && user?.role === 'guest')
                ? fetch(`${API_BASE_URL}/api/guests/profile`, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                }).then(res => res.json())
                : Promise.resolve(null);

            const [feedData, wData, pData] = await Promise.all([feedPromise, weddingPromise, profilePromise]);

            if (feedData?.posts) {
                setPosts(feedData.posts);
                setError(null);
            } else if (posts.length === 0) {
                setError(feedData?.message || 'Failed to load feed');
            }

            if (wData?.wedding) {
                setWeddingData(wData.wedding);
            }

            if (pData?.guest) {
                const freshGuest = pData.guest;
                const updatedUser = {
                    ...user,
                    ...freshGuest,
                    id: freshGuest._id || user?.id,
                    profilePhoto: freshGuest.profilePhoto || user?.profilePhoto
                };

                if (JSON.stringify(user) !== JSON.stringify(updatedUser)) {
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    setUser(updatedUser);
                }
            }
        } catch (err) {
            console.error('Fetch error:', err);
            if (posts.length === 0) {
                setError('Failed to load. Please try again.');
            }
        } finally {
            if (isInitial) setLoading(false);
        }
    }, [accessToken, user, setUser, posts.length]);

    // Fetch reactions batch when posts change
    useEffect(() => {
        if (posts.length > 0 && accessToken) {
            const postIds = posts.filter((p: any) => !p.isBlessing).map((p: any) => p._id);
            if (postIds.length === 0) return;

            fetch(`${API_BASE_URL}/api/reactions/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ postIds }),
            })
                .then(res => res.json())
                .then(data => {
                    if (data.reactionsByPost) setReactionsByPost(data.reactionsByPost);
                    if (data.cheersByPost) setCheersByPost(data.cheersByPost);
                })
                .catch(err => console.error('Error fetching reactions:', err));
        }
    }, [posts, accessToken]);

    const handleToggleReaction = async (postId: string, type: string) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/reactions/${postId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ type }),
            });
            const data = await res.json();
            if (data.reactions) {
                setReactionsByPost(prev => ({ ...prev, [postId]: data.reactions }));
            }
        } catch (err) {
            console.error('Error toggling reaction:', err);
        }
    };

    const handleCheer = async (postId: string) => {
        // Create flying particles
        const emojis = ['🌸', '💐', '✨', '❤️', '🎊', '🌺', '💖', '🎉'];
        const newParticles = Array.from({ length: 6 }, () => {
            cheerIdRef.current += 1;
            return {
                id: cheerIdRef.current,
                postId,
                emoji: emojis[Math.floor(Math.random() * emojis.length)],
                x: Math.random() * 80 + 10,
                delay: Math.random() * 0.3,
            };
        });
        setCheerParticles(prev => [...prev, ...newParticles]);

        // Remove particles after animation
        setTimeout(() => {
            setCheerParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
        }, 2000);

        // Fire to backend
        try {
            const res = await fetch(`${API_BASE_URL}/api/reactions/${postId}/cheer`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (data.totalCheers !== undefined) {
                setCheersByPost(prev => ({ ...prev, [postId]: data.totalCheers }));
            }
        } catch (err) {
            console.error('Error sending cheer:', err);
        }
    };

    const getReactionCount = (postId: string, type: string) => {
        const reactions = reactionsByPost[postId] || [];
        return reactions.filter((r: any) => r.type === type).length;
    };

    const hasUserReacted = (postId: string, type: string) => {
        const reactions = reactionsByPost[postId] || [];
        return reactions.some((r: any) => r.userId === user?.id && r.type === type);
    };

    const handlePayShagun = async () => {
        if (!weddingData?.upiId) {
            alert('UPI ID not set for this wedding.');
            return;
        }

        const upiId = weddingData.upiId;
        const upiName = weddingData.upiName || weddingData.coupleName || 'Wedding Shagun';
        const amount = shagunAmount;
        const note = shagunNote || `Shagun for ${weddingData.coupleName}`;

        const upiUrl = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(upiName)}&am=${encodeURIComponent(amount)}&tn=${encodeURIComponent(note)}&cu=INR`;
        window.location.href = upiUrl;

        try {
            const res = await fetch(`${API_BASE_URL}/api/blessings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    message: note,
                    amount: amount
                })
            });

            if (res.ok) {
                fetchFeedData(false);
                setShagunModalOpen(false);
                alert('Your Blessing Card has been posted! ❤️');
            }
        } catch (err) {
            console.error('Error posting blessing:', err);
        }
    };

    const toggleMusic = () => {
        if (audioRef.current) {
            if (isMusicPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch(err => console.error("Audio play failed:", err));
            }
            setIsMusicPlaying(!isMusicPlaying);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchFeedData(true);
                const interval = setInterval(() => {
                    fetchFeedData(false);
                }, 10000);
                return () => clearInterval(interval);
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, fetchFeedData, history]);

    if (loading) return (
        <div className="feed-container loading-state">
            <div className="shimmer-card"></div>
            <p>Gathering wedding moments...</p>
        </div>
    );

    if (error) return (
        <div className="feed-container error-state">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    const userName = user?.name || 'Guest';

    return (
        <div className="guest-feed-page">
            {weddingData?.themeSongUrl && (
                <audio
                    ref={audioRef}
                    src={weddingData.themeSongUrl}
                    loop
                    onPlay={() => setIsMusicPlaying(true)}
                    onPause={() => setIsMusicPlaying(false)}
                />
            )}

            {/* Music FAB — always visible */}
            <div className="music-toggle-fab" onClick={toggleMusic}>
                {isMusicPlaying ? <Volume2 size={20} /> : <VolumeX size={20} />}
                <div className="music-waves">
                    <span className={isMusicPlaying ? 'wave playing' : 'wave'}></span>
                    <span className={isMusicPlaying ? 'wave playing' : 'wave'}></span>
                    <span className={isMusicPlaying ? 'wave playing' : 'wave'}></span>
                </div>
            </div>

            {/* feed-header: visible in old guest layout, hidden by CSS when AdminHeader is active */}
            <header className="feed-header">
                <div className="header-content">
                    <h1>Wedding Feed</h1>
                    <p>Exclusive updates from {weddingData?.coupleName || 'the Couple'}</p>
                </div>
            </header>

            {/* Guest profile bar is now rendered globally in App.tsx for all guest pages */}

            <main className="feed-container">
                {posts.length === 0 ? (
                    <div className="empty-feed">
                        <span className="empty-icon">📸</span>
                        <h3>No posts yet</h3>
                        <p>Stay tuned! The wedding celebrations are about to begin.</p>
                        <div style={{ marginTop: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '8px', fontSize: '0.8rem', color: '#666' }}>
                            💡 <strong>Tip for Admin:</strong> Make sure to click the <strong>"Share to Guest Feed"</strong> button when creating a post to make it visible here.
                        </div>
                    </div>
                ) : (
                    <div className="posts-list">
                        {posts.map((post: any) => (
                            post.isBlessing ? (
                                <div key={post._id} className="blessing-card-item">
                                    <div className="blessing-card-content">
                                        <div className="blessing-icon">🧧</div>
                                        <div className="blessing-text">
                                            <p className="blessing-message">"{post.caption}"</p>
                                            <p className="blessing-author">— {post.guestName}</p>
                                        </div>
                                        <div className="blessing-badge">Wall of Love</div>
                                    </div>
                                    <div className="blessing-footer">
                                        <span className="blessing-date">{new Date(post.createdAt).toLocaleDateString()}</span>
                                        <div className="blessing-reactions">❤️ 🙏 ✨</div>
                                    </div>
                                </div>
                            ) : (
                                <div key={post._id} className="insta-post-card" style={{ position: 'relative', overflow: 'hidden' }}>
                                    {/* Flying Cheer Particles */}
                                    {cheerParticles
                                        .filter(p => p.postId === post._id)
                                        .map(particle => (
                                            <span
                                                key={particle.id}
                                                className="cheer-particle"
                                                style={{
                                                    left: `${particle.x}%`,
                                                    animationDelay: `${particle.delay}s`,
                                                }}
                                            >
                                                {particle.emoji}
                                            </span>
                                        ))}

                                    <div className="post-header">
                                        <div className="post-user-info">
                                            <div className="user-avatar">✨</div>
                                            <div className="user-meta">
                                                <span className="user-name">Wedding Update</span>
                                                <span className="post-date">{new Date(post.createdAt).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                        <div className="post-more">•••</div>
                                    </div>

                                    {post.imageUrl && (
                                        <div className="post-image-container">
                                            <img src={getImageUrl(post.imageUrl)} alt="Post content" />
                                        </div>
                                    )}

                                    <div className="post-content">
                                        <p className="post-caption">
                                            <span className="caption-user">Wedding Update</span> {post.caption}
                                        </p>
                                    </div>

                                    {/* Social Cheer Reaction Bar */}
                                    <div className="social-cheer-bar">
                                        <div className="reaction-pills">
                                            {REACTION_TYPES.map(rt => {
                                                const count = getReactionCount(post._id, rt.type);
                                                const active = hasUserReacted(post._id, rt.type);
                                                return (
                                                    <button
                                                        key={rt.type}
                                                        className={`reaction-pill ${active ? 'active' : ''}`}
                                                        onClick={() => handleToggleReaction(post._id, rt.type)}
                                                        title={rt.label}
                                                    >
                                                        <span className="reaction-emoji">{rt.emoji}</span>
                                                        {count > 0 && <span className="reaction-count">{count}</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <button
                                            className="cheer-burst-btn"
                                            onClick={() => handleCheer(post._id)}
                                        >
                                            <span className="cheer-icon">🎊</span>
                                            <span className="cheer-label">Cheer!</span>
                                            {(cheersByPost[post._id] || 0) > 0 && (
                                                <span className="cheer-total">{cheersByPost[post._id]}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                )}
            </main>

            <div className="fab-container">
                <div className="album-fab" onClick={() => history.push('/guest/gallery')}>
                    <span>🖼️</span>
                    <span>GALLERY</span>
                </div>
                <div className="shagun-fab" onClick={() => setShagunModalOpen(true)}>
                    <span>🧧</span>
                    <span>SHAGUN</span>
                </div>
                <div className="album-fab" onClick={() => history.push('/guest/lost-found')} style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <span>🔍</span>
                    <span>LOST</span>
                </div>
                <div className="album-fab" onClick={() => history.push('/guest/menu')} style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                    <span>🍽️</span>
                    <span>MENU</span>
                </div>
            </div>

            {shagunModalOpen && (
                <div className="shagun-modal-overlay" onClick={() => setShagunModalOpen(false)}>
                    <div className="shagun-envelope" onClick={e => e.stopPropagation()}>
                        <button className="close-modal" onClick={() => setShagunModalOpen(false)}>&times;</button>
                        <div className="envelope-header">
                            <h2>Digital Shagun</h2>
                            <p>Send your blessings to {weddingData?.coupleName}</p>
                        </div>
                        <div className="envelope-body">
                            {!weddingData?.upiId ? (
                                <div className="no-upi-msg">
                                    <p>The couple hasn't set up their UPI details yet.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <label>Shagun Amount</label>
                                        <div className="shagun-amount-wrapper">
                                            <span className="currency-symbol">₹</span>
                                            <input type="number" value={shagunAmount} onChange={e => setShagunAmount(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Personalized Note</label>
                                        <textarea value={shagunNote} onChange={e => setShagunNote(e.target.value)} />
                                    </div>
                                    <button className="pay-button" onClick={handlePayShagun}>🧧 Send Shagun</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GuestDashboard;
